import { useEffect, useState, useCallback, useRef } from 'react';
const DEFAULT_OPTIONS = {
    healthCheckUrl: '/api/health',
    healthCheckInterval: 30000, // 30 seconds
    enableHealthCheck: false, // Disabled by default to avoid unnecessary requests
};
export const useNetworkStatus = (options = {}) => {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    const [networkStatus, setNetworkStatus] = useState(() => ({
        isOnline: navigator.onLine,
        wasOffline: false,
        connectionQuality: navigator.onLine ? 'good' : 'offline',
    }));
    const healthCheckTimeoutRef = useRef(null);
    const lastOnlineTimeRef = useRef(navigator.onLine ? new Date() : undefined);
    const performHealthCheck = useCallback(async () => {
        if (!opts.enableHealthCheck || !opts.healthCheckUrl) {
            return;
        }
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout for health check
            const response = await fetch(opts.healthCheckUrl, {
                method: 'HEAD',
                signal: controller.signal,
                cache: 'no-cache',
            });
            clearTimeout(timeoutId);
            if (response.ok) {
                setNetworkStatus(prev => ({
                    ...prev,
                    isOnline: true,
                    connectionQuality: 'good',
                    lastOnlineTime: new Date(),
                }));
                lastOnlineTimeRef.current = new Date();
            }
            else {
                setNetworkStatus(prev => ({
                    ...prev,
                    isOnline: false,
                    connectionQuality: 'offline',
                }));
            }
        }
        catch (error) {
            // Health check failed - likely offline
            setNetworkStatus(prev => ({
                ...prev,
                isOnline: false,
                connectionQuality: 'offline',
            }));
        }
    }, [opts.enableHealthCheck, opts.healthCheckUrl]);
    const handleOnline = useCallback(() => {
        const now = new Date();
        setNetworkStatus(prev => ({
            isOnline: true,
            wasOffline: !prev.isOnline,
            lastOnlineTime: now,
            connectionQuality: 'good',
        }));
        lastOnlineTimeRef.current = now;
    }, []);
    const handleOffline = useCallback(() => {
        setNetworkStatus(prev => ({
            isOnline: false,
            wasOffline: prev.isOnline,
            connectionQuality: 'offline',
        }));
    }, []);
    useEffect(() => {
        // Set initial state
        if (navigator.onLine && !lastOnlineTimeRef.current) {
            lastOnlineTimeRef.current = new Date();
        }
        // Listen to browser online/offline events
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        // Optional: Set up periodic health check
        if (opts.enableHealthCheck) {
            const scheduleHealthCheck = () => {
                if (healthCheckTimeoutRef.current) {
                    clearTimeout(healthCheckTimeoutRef.current);
                }
                healthCheckTimeoutRef.current = window.setTimeout(() => {
                    performHealthCheck();
                    scheduleHealthCheck();
                }, opts.healthCheckInterval);
            };
            scheduleHealthCheck();
        }
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            if (healthCheckTimeoutRef.current) {
                clearTimeout(healthCheckTimeoutRef.current);
            }
        };
    }, [handleOnline, handleOffline, performHealthCheck, opts.enableHealthCheck, opts.healthCheckInterval]);
    return networkStatus;
};
export default useNetworkStatus;
